import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Treemap,
  Tooltip as RechartsTooltip
} from 'recharts';
import { AuthContext } from '../contexts/AuthContext';
import { LineGraph, Scorecard, PieGraph } from '../components/utility/Charts';
import AnalyticsSourceSelector from '../components/AnalyticsSourceSelector';
import {
  StoreDateRangeFilter,
  fillDates,
  fillMonths,
  TablePagination,
  Caret,
  ToolTip
} from '../components/utility/Utility';
import {
  storeApiBoundsFromMatch,
  storeMatchRangeLastNCalendarDays,
} from '../utils/storeDateRange';
import {
  CUSTOM_RANGE_LABEL,
  DEFAULT_RANGE_LABEL,
  RANGE_PRESETS,
  applyUrlFiltersToSeed,
  parseViewState,
  resolveRangeFromUrl,
  serializeDetailViewState,
  serializeViewState
} from '../utils/productAnalysisUrlState';
import '../assets/css/components/program.css';
import '../assets/css/charts.css';
import loadingBuffering from '../assets/images/loading-buffering.gif';
import ellipsisVerticalIcon from '../assets/images/icons/ellipsis-vertical-solid.svg';

axios.defaults.withCredentials = true;

const TIME_GRAIN_OPTIONS = [
  { id: 'month', label: 'Month' },
  { id: 'day', label: 'Day' },
  { id: 'weekday', label: 'Day of Week' },
  { id: 'hour', label: 'Hour' }
];

/** Line-chart series metrics (full set returned on each sales_over_time point). */
const SERIES_METRICS = {
  NetSales: { id: 'NetSales', label: 'Net Sales', format: 'money' },
  Orders: { id: 'Orders', label: 'Orders', format: 'count' },
  UnitsSold: { id: 'UnitsSold', label: 'Units Sold', format: 'count' },
  AvgSalePrice: { id: 'AvgSalePrice', label: 'Avg Sale Price', format: 'money' },
  Refunds: { id: 'Refunds', label: 'Refunds', format: 'money' },
  ReturningCustomerRate: { id: 'ReturningCustomerRate', label: 'Returning Customer %', format: 'percent' },
  OpportunityCost: { id: 'OpportunityCost', label: 'Opportunity Cost', format: 'money' },
  LostUnits: { id: 'LostUnits', label: 'Lost Units', format: 'decimal' }
};

const SERIES_METRIC_OPTIONS = Object.values(SERIES_METRICS);
const OPPORTUNITY_METRIC_IDS = new Set(['OpportunityCost', 'LostUnits']);
const DEFAULT_LEFT_METRIC = 'NetSales';
const DEFAULT_RIGHT_METRIC = 'ReturningCustomerRate';

const EMPTY_SERIES_POINT = {
  NetSales: 0,
  Orders: 0,
  UnitsSold: 0,
  AvgSalePrice: 0,
  Refunds: 0,
  ReturningCustomerRate: 0,
  OpportunityCost: 0,
  LostUnits: 0
};

const compactMoneyTick = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

function formatSeriesTooltip(metricId, value, dollarFormat, numberFormat) {
  const fmt = SERIES_METRICS[metricId]?.format;
  if (fmt === 'money') return dollarFormat.format(value || 0);
  if (fmt === 'percent') return `${Math.round((value || 0) * 1000) / 10}%`;
  if (fmt === 'decimal') return numberFormat.format(Math.round((value || 0) * 10) / 10);
  return numberFormat.format(value || 0);
}

function formatSeriesTick(metricId, value) {
  const fmt = SERIES_METRICS[metricId]?.format;
  if (fmt === 'money') return compactMoneyTick.format(value || 0);
  if (fmt === 'percent') return `${Math.round((value || 0) * 100)}%`;
  if (fmt === 'decimal') {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value || 0);
  }
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value || 0);
}

const MONTH_TICK_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

function formatTimeGrainTick(value, grain) {
  const s = String(value ?? '');
  if (grain === 'month') {
    const [y, m] = s.split('-');
    const mi = parseInt(m, 10);
    if (!y || !Number.isFinite(mi)) return s;
    return `${MONTH_TICK_LABELS[mi - 1] || m} ${String(y).slice(-2)}`;
  }
  if (grain === 'weekday' || grain === 'hour') return s;
  const parts = s.split('-');
  if (parts.length >= 3) return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
  return s;
}

function formatTimeGrainLabel(value, grain) {
  const s = String(value ?? '');
  if (grain === 'month') {
    const [y, m] = s.split('-');
    const mi = parseInt(m, 10);
    if (!y || !Number.isFinite(mi)) return s;
    return `${MONTH_TICK_LABELS[mi - 1] || m} ${y}`;
  }
  if (grain === 'weekday' || grain === 'hour') return s;
  const parts = s.split('-');
  if (parts.length >= 3) {
    return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}/${parts[0]}`;
  }
  return s;
}

function sortJoin(arr) {
  return [...arr].map(String).sort().join('\u0001');
}

function compareFacetLabel(a, b) {
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

/** Facet options from API plus any selected values not in that list (kept after date range change). */
function mergedFacetOptions(options, selected) {
  const out = [];
  const seen = new Set();
  for (const o of options || []) {
    const s = String(o);
    if (!seen.has(s)) {
      seen.add(s);
      out.push(o);
    }
  }
  for (const x of selected || []) {
    const s = String(x);
    if (!seen.has(s)) {
      seen.add(s);
      out.push(x);
    }
  }
  return out;
}

export function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  showSearch = false,
  searchPlaceholder = 'Search…',
  sortAlphabetically = false,
  disabled = false
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(() => [...value]);
  const ref = useRef(null);
  const selectAllRef = useRef(null);
  const draftRef = useRef(value);
  const valueRef = useRef(value);
  const openRef = useRef(false);
  const closePanelRef = useRef(() => {});

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!open) {
      setDraft([...value]);
    }
  }, [value, open]);

  useEffect(() => {
    closePanelRef.current = () => {
      const next = draftRef.current;
      const rowOpts = mergedFacetOptions(options, valueRef.current);
      if (rowOpts.length > 0 && next.length === 0) {
        setOpen(false);
        return;
      }
      if (sortJoin(next) !== sortJoin(valueRef.current)) {
        onChange([...next]);
      }
      setOpen(false);
    };
  }, [options, onChange]);

  useEffect(() => {
    const fn = (e) => {
      if (!openRef.current) return;
      if (ref.current && !ref.current.contains(e.target)) closePanelRef.current();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  const rowOptions = useMemo(() => mergedFacetOptions(options, value), [options, value]);

  const filteredOptions = useMemo(() => {
    let list = rowOptions;
    if (showSearch && search.trim()) {
      const q = search.trim().toLowerCase();
      list = rowOptions.filter((o) => String(o).toLowerCase().includes(q));
    }
    if (sortAlphabetically) {
      list = [...list].sort(compareFacetLabel);
    }
    return list;
  }, [rowOptions, search, showSearch, sortAlphabetically]);

  const sel = open ? draft : value;
  const selSet = useMemo(() => new Set(sel.map(String)), [sel]);
  // Trigger summary: full facet universe.
  const allSelected = options.length > 0 && options.every((o) => selSet.has(String(o)));
  const noneSelected = sel.length === 0;
  // Header checkbox: only the currently visible (search-filtered) rows.
  const visibleAllSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selSet.has(String(o)));
  const visibleNoneSelected =
    filteredOptions.length === 0 || filteredOptions.every((o) => !selSet.has(String(o)));
  const visiblePartial = !visibleAllSelected && !visibleNoneSelected && filteredOptions.length > 0;
  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = visiblePartial;
  }, [visiblePartial, open]);

  const toggle = (opt) => {
    const set = new Set(draft);
    if (set.has(opt)) set.delete(opt);
    else set.add(opt);
    setDraft([...set]);
  };

  const selectOnly = (opt) => {
    setDraft([opt]);
  };

  const onHeaderChange = () => {
    if (visibleAllSelected) {
      const remove = new Set(filteredOptions.map(String));
      setDraft(draft.filter((o) => !remove.has(String(o))));
    } else {
      const next = new Set(draft.map(String));
      const out = [...draft];
      for (const o of filteredOptions) {
        if (!next.has(String(o))) {
          next.add(String(o));
          out.push(o);
        }
      }
      setDraft(out);
    }
  };

  const summary =
    rowOptions.length === 0 && sel.length === 0
      ? '0'
      : rowOptions.length === 0
        ? `${sel.length} selected`
        : noneSelected
          ? 'None'
          : allSelected
            ? 'All'
            : sel.length <= 2
              ? sel.join(', ')
              : `${sel.length} selected`;

  return (
    <div className="product-analysis-filters__multiselect" ref={ref}>
      <button
        type="button"
        className="product-analysis-filters__multiselect-trigger"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) closePanelRef.current();
          else {
            setDraft([...value]);
            setOpen(true);
          }
        }}
      >
        {label}: {summary}
      </button>
      {open && !disabled && (
        <div className="product-analysis-filters__multiselect-panel" role="listbox">
          {rowOptions.length === 0 ? (
            <div className="product-analysis-filters__multiselect-empty">No values in range</div>
          ) : (
            <>
              <div className="product-analysis-filters__multiselect-panel-header">
                <label className="product-analysis-filters__multiselect-selectall">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={visibleAllSelected}
                    onChange={onHeaderChange}
                  />
                  <span>{label}</span>
                </label>
              </div>
              {showSearch && (
                <input
                  type="search"
                  className="product-analysis-filters__multiselect-search"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Search ${label}`}
                />
              )}
              {filteredOptions.length === 0 ? (
                <div className="product-analysis-filters__multiselect-empty">No matches</div>
              ) : (
                filteredOptions.map((opt) => (
                  <div key={String(opt)} className="product-analysis-filters__multiselect-option">
                    <label className="product-analysis-filters__multiselect-option-main">
                      <input type="checkbox" checked={draft.includes(opt)} onChange={() => toggle(opt)} />
                      <span>{opt}</span>
                    </label>
                    <button
                      type="button"
                      className="product-analysis-filters__multiselect-option-only"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectOnly(opt);
                      }}
                      aria-label={`Only ${opt}`}
                    >
                      ONLY
                    </button>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Searchable SKU multiselect; defaults to master SKU with optional variant SKU toggle. */
export function SkuMultiSelectFilter({
  variantOptions,
  masterOptions,
  skuMode,
  value,
  onChange,
  onModeChange,
  disabled = false
}) {
  const options = skuMode === 'master' ? masterOptions : variantOptions;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(() => [...value]);
  const ref = useRef(null);
  const selectAllRef = useRef(null);
  const draftRef = useRef(value);
  const valueRef = useRef(value);
  const openRef = useRef(false);
  const closePanelRef = useRef(() => {});

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!open) {
      setDraft([...value]);
    }
  }, [value, open]);

  useEffect(() => {
    closePanelRef.current = () => {
      const next = draftRef.current;
      const rowOpts = mergedFacetOptions(options, valueRef.current);
      if (rowOpts.length > 0 && next.length === 0) {
        setOpen(false);
        return;
      }
      if (sortJoin(next) !== sortJoin(valueRef.current)) {
        onChange([...next]);
      }
      setOpen(false);
    };
  }, [options, onChange]);

  useEffect(() => {
    const fn = (e) => {
      if (!openRef.current) return;
      if (ref.current && !ref.current.contains(e.target)) closePanelRef.current();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const rowOptions = useMemo(() => mergedFacetOptions(options, value), [options, value]);

  const filteredOptions = useMemo(() => {
    let list = rowOptions;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = rowOptions.filter((o) => String(o).toLowerCase().includes(q));
    }
    return [...list].sort(compareFacetLabel);
  }, [rowOptions, search]);

  const sel = open ? draft : value;
  const selSet = useMemo(() => new Set(sel.map(String)), [sel]);
  const allSelected = options.length > 0 && options.every((o) => selSet.has(String(o)));
  const noneSelected = sel.length === 0;
  const visibleAllSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selSet.has(String(o)));
  const visibleNoneSelected =
    filteredOptions.length === 0 || filteredOptions.every((o) => !selSet.has(String(o)));
  const visiblePartial = !visibleAllSelected && !visibleNoneSelected && filteredOptions.length > 0;
  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = visiblePartial;
  }, [visiblePartial, open]);

  const toggle = (opt) => {
    const set = new Set(draft);
    if (set.has(opt)) set.delete(opt);
    else set.add(opt);
    setDraft([...set]);
  };

  const selectOnly = (opt) => {
    setDraft([opt]);
  };

  const onHeaderChange = () => {
    if (visibleAllSelected) {
      const remove = new Set(filteredOptions.map(String));
      setDraft(draft.filter((o) => !remove.has(String(o))));
    } else {
      const next = new Set(draft.map(String));
      const out = [...draft];
      for (const o of filteredOptions) {
        if (!next.has(String(o))) {
          next.add(String(o));
          out.push(o);
        }
      }
      setDraft(out);
    }
  };

  const summary =
    rowOptions.length === 0 && sel.length === 0
      ? '0'
      : rowOptions.length === 0
        ? `${sel.length} selected`
        : noneSelected
          ? 'None'
          : allSelected
            ? 'All'
            : sel.length <= 2
              ? sel.join(', ')
              : `${sel.length} selected`;

  useEffect(() => {
    setDraft([...value]);
  }, [skuMode, value]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  return (
    <div className="product-analysis-filters__multiselect" ref={ref}>
      <button
        type="button"
        className="product-analysis-filters__multiselect-trigger"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) closePanelRef.current();
          else {
            setDraft([...value]);
            setOpen(true);
          }
        }}
      >
        SKU: {summary}
      </button>
      {open && !disabled && (
        <div className="product-analysis-filters__multiselect-panel" role="listbox">
          {rowOptions.length === 0 ? (
            <div className="product-analysis-filters__multiselect-empty">No SKUs in range</div>
          ) : (
            <>
              <div className="product-analysis-filters__multiselect-panel-header product-analysis-filters__sku-panel-header">
                <label className="product-analysis-filters__multiselect-selectall">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={visibleAllSelected}
                    onChange={onHeaderChange}
                  />
                  <span>SKU</span>
                </label>
                <div className="product-analysis-filters__sku-mode-toggle">
                  <span className="product-analysis-filters__sku-mode-label">Variant SKU</span>
                  <label className="switch">
                    <input
                      className="switch-input"
                      type="checkbox"
                      checked={skuMode === 'variant'}
                      onChange={(e) => {
                        if (disabled) return;
                        const nextMode = e.target.checked ? 'variant' : 'master';
                        if (nextMode !== skuMode) {
                          onModeChange(nextMode);
                        }
                      }}
                      disabled={disabled}
                      aria-label="Use variant SKU list"
                    />
                    <span className="slider" />
                  </label>
                </div>
              </div>
              <input
                type="search"
                className="product-analysis-filters__multiselect-search"
                placeholder="Search SKUs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Search SKUs"
              />
              {filteredOptions.length === 0 ? (
                <div className="product-analysis-filters__multiselect-empty">No matches</div>
              ) : (
                filteredOptions.map((opt) => (
                  <div key={String(opt)} className="product-analysis-filters__multiselect-option">
                    <label className="product-analysis-filters__multiselect-option-main">
                      <input type="checkbox" checked={draft.includes(opt)} onChange={() => toggle(opt)} />
                      <span>{opt}</span>
                    </label>
                    <button
                      type="button"
                      className="product-analysis-filters__multiselect-option-only"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectOnly(opt);
                      }}
                      aria-label={`Only ${opt}`}
                    >
                      ONLY
                    </button>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Generic multi-select for `[{id, name}]` options where `value` is an array of stringified ids.
 * Used by the locations filter and the product-title filter (titles are passed in as `{id, name: title}`).
 *
 * The wire payload is just `id`s, so this filter never bloats the URL — the backend looks up
 * names via the indexed `shopify_product_id` / `shopify_location_id` rather than receiving them.
 */
export function IdNameMultiSelectFilter({
  label,
  options,
  value,
  onChange,
  showSearch = false,
  searchPlaceholder = 'Search…',
  emptyText = 'No values in range',
  maxSelection = null,
  disabled = false,
  allowEmpty = false
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(() => [...value]);
  const ref = useRef(null);
  const selectAllRef = useRef(null);
  const draftRef = useRef(value);
  const valueRef = useRef(value);
  const openRef = useRef(false);
  const closePanelRef = useRef(() => {});
  const nameCacheRef = useRef(new Map());

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!open) {
      setDraft([...value]);
    }
  }, [value, open]);

  useEffect(() => {
    closePanelRef.current = () => {
      const next = draftRef.current;
      const capped =
        maxSelection != null && maxSelection > 0 ? next.slice(0, maxSelection) : next;
      const apiIds = (options || []).map((o) => String(o.id));
      // Default (Meta Ads / Shopify): refuse "none selected" so analytics always has a universe.
      // Klaviyo passes allowEmpty so campaigns or flows can be fully deselected.
      if (apiIds.length > 0 && capped.length === 0 && !allowEmpty) {
        setOpen(false);
        return;
      }
      if (sortJoin(capped) !== sortJoin(valueRef.current)) {
        onChange([...capped]);
      }
      setOpen(false);
    };
  }, [options, onChange, maxSelection, allowEmpty]);

  useEffect(() => {
    const fn = (e) => {
      if (!openRef.current) return;
      if (ref.current && !ref.current.contains(e.target)) closePanelRef.current();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    for (const o of options || []) {
      const id = String(o.id);
      const name = String(o.name || '').trim();
      if (id && name) nameCacheRef.current.set(id, name);
    }
  }, [options]);

  const nameById = useMemo(() => {
    const m = new Map();
    nameCacheRef.current.forEach((name, id) => m.set(id, name));
    (options || []).forEach((o) => {
      const id = String(o.id);
      const name = String(o.name || '').trim();
      m.set(id, name || id);
    });
    (value || []).forEach((id) => {
      const s = String(id);
      if (!m.has(s)) m.set(s, s);
    });
    return m;
  }, [options, value]);

  const allIds = useMemo(() => (options || []).map((o) => String(o.id)), [options]);

  const displayIdsSorted = useMemo(() => {
    const set = new Set(allIds);
    for (const v of value || []) set.add(String(v));
    return [...set].sort((a, b) => compareFacetLabel(nameById.get(a) || a, nameById.get(b) || b));
  }, [allIds, value, nameById]);

  const filteredIds = useMemo(() => {
    if (!showSearch || !search.trim()) return displayIdsSorted;
    const q = search.trim().toLowerCase();
    return displayIdsSorted.filter((idStr) => {
      const labelText = (nameById.get(idStr) || idStr).toLowerCase();
      return labelText.includes(q) || idStr.toLowerCase().includes(q);
    });
  }, [displayIdsSorted, nameById, search, showSearch]);

  const sel = open ? draft : value;
  const selSet = useMemo(() => new Set(sel.map(String)), [sel]);
  const allSelected = allIds.length > 0 && allIds.every((idStr) => selSet.has(idStr));
  const noneSelected = sel.length === 0;
  const visibleAllSelected =
    filteredIds.length > 0 && filteredIds.every((idStr) => selSet.has(idStr));
  const visibleNoneSelected =
    filteredIds.length === 0 || filteredIds.every((idStr) => !selSet.has(idStr));
  const visiblePartial = !visibleAllSelected && !visibleNoneSelected && filteredIds.length > 0;
  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = visiblePartial;
  }, [visiblePartial, open]);

  const capSelection = (ids) => {
    if (maxSelection == null || maxSelection < 1) return ids;
    return ids.slice(0, maxSelection);
  };

  const atMaxSelection =
    maxSelection != null && maxSelection > 0 && draft.length >= maxSelection;

  const toggle = (idStr) => {
    const set = new Set(draft);
    if (set.has(idStr)) set.delete(idStr);
    else {
      if (atMaxSelection) return;
      set.add(idStr);
    }
    setDraft([...set]);
  };

  const selectOnlyId = (idStr) => {
    setDraft([idStr]);
  };

  const onHeaderChange = () => {
    if (visibleAllSelected) {
      const remove = new Set(filteredIds);
      setDraft(draft.filter((id) => !remove.has(String(id))));
    } else {
      const next = new Set(draft.map(String));
      const out = [...draft];
      for (const idStr of filteredIds) {
        if (!next.has(idStr)) {
          next.add(idStr);
          out.push(idStr);
        }
      }
      setDraft(capSelection(out));
    }
  };

  const summary =
    allIds.length === 0 && sel.length === 0
      ? '0'
      : noneSelected
        ? 'None'
        : allSelected
          ? 'All'
          : sel.length <= 2
            ? sel.map((id) => nameById.get(id) || id).join(', ')
            : `${sel.length} selected`;

  return (
    <div className="product-analysis-filters__multiselect" ref={ref}>
      <button
        type="button"
        className="product-analysis-filters__multiselect-trigger"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) closePanelRef.current();
          else {
            setDraft([...value]);
            setOpen(true);
          }
        }}
      >
        {label}: {summary}
      </button>
      {open && !disabled && (
        <div className="product-analysis-filters__multiselect-panel" role="listbox">
          {displayIdsSorted.length === 0 ? (
            <div className="product-analysis-filters__multiselect-empty">{emptyText}</div>
          ) : (
            <>
              {allIds.length > 0 && (
                <div className="product-analysis-filters__multiselect-panel-header">
                  <label className="product-analysis-filters__multiselect-selectall">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={visibleAllSelected}
                      onChange={onHeaderChange}
                    />
                    <span>{label}</span>
                  </label>
                </div>
              )}
              {showSearch && (
                <input
                  type="search"
                  className="product-analysis-filters__multiselect-search"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Search ${label}`}
                />
              )}
              {filteredIds.length === 0 ? (
                <div className="product-analysis-filters__multiselect-empty">No matches</div>
              ) : (
                filteredIds.map((idStr) => {
                  const displayName = nameById.get(idStr) || idStr;
                  const checked = draft.includes(idStr);
                  const optionDisabled = !checked && atMaxSelection;
                  return (
                    <div key={idStr} className="product-analysis-filters__multiselect-option">
                      <label className="product-analysis-filters__multiselect-option-main">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={optionDisabled}
                          onChange={() => toggle(idStr)}
                        />
                        <span>{displayName}</span>
                      </label>
                      <button
                        type="button"
                        className="product-analysis-filters__multiselect-option-only"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectOnlyId(idStr);
                        }}
                        aria-label={`Only ${displayName}`}
                      >
                        ONLY
                      </button>
                    </div>
                  );
                })
              )}
              {maxSelection != null && maxSelection > 0 && (
                <div className="product-analysis-filters__multiselect-limit">
                  {atMaxSelection
                    ? `Maximum of ${maxSelection} selected.`
                    : `Select up to ${maxSelection}.`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Backwards-compatible shim around `IdNameMultiSelectFilter`. Existing call sites
 * (notably the detail view) pass `locations` instead of the generic `options` prop.
 */
export function LocationMultiSelectFilter({ locations, ...rest }) {
  return <IdNameMultiSelectFilter options={locations} emptyText="No locations in range" {...rest} />;
}

export function filtersAllowAnalytics(facets, filters) {
  const F = (x) => (Array.isArray(x) ? x : []);
  const skuMode = filters?.sku_mode === 'variant' ? 'variant' : 'master';
  const skuFacetList = skuMode === 'master' ? F(facets?.master_skus) : F(facets?.skus);
  const pairs = [
    // Title filter is now keyed by product id; facet shape is `[{id, name}]`.
    [F(facets?.titles).map((t) => String(t.id)), F(filters?.product_ids)],
    [F(facets?.collections).map((c) => String(c.id)), F(filters?.collection_ids)],
    [F(facets?.product_types), F(filters?.product_types)],
    [F(facets?.tags), F(filters?.tags)],
    [F(facets?.sales_channels), F(filters?.sales_channels)],
    [F(facets?.locations).map((l) => String(l.id)), F(filters?.location_ids)],
    [skuFacetList, F(filters?.skus)]
  ];
  for (const [opts, sel] of pairs) {
    if (opts.length > 0 && (!sel || sel.length === 0)) return false;
  }
  return true;
}

/** Shallow snapshot of facet option lists at filter-candidates request time. */
export function snapshotFacetLists(facetState) {
  return {
    titles: [...(facetState.titles || [])],
    collections: [...(facetState.collections || [])],
    product_types: [...(facetState.product_types || [])],
    tags: [...(facetState.tags || [])],
    sales_channels: [...(facetState.sales_channels || [])],
    locations: [...(facetState.locations || [])],
    skus: [...(facetState.skus || [])],
    master_skus: [...(facetState.master_skus || [])]
  };
}

/** Whether `selected` is exactly the full option list from the pre-request snapshot (same multiset). Empty snapshot + empty selection counts as "all". */
function selectionMatchesFullSnapshotOptions(selected, snapshotOptions) {
  const snap = Array.isArray(snapshotOptions) ? snapshotOptions : [];
  const sel = Array.isArray(selected) ? selected : [];
  const sSnap = snap.map(String);
  const sSel = sel.map(String);
  if (snap.length === 0) return sel.length === 0;
  return sel.length === snap.length && sortJoin(sSel) === sortJoin(sSnap);
}

/** Same but the snapshot is `[{id, name}]` and selected is an array of stringified ids. */
function selectionMatchesFullSnapshotIdNameOptions(selectedIds, snapshotIdNameOptions) {
  const snap = Array.isArray(snapshotIdNameOptions) ? snapshotIdNameOptions : [];
  const snapIds = snap.map((o) => String(o.id));
  const sel = Array.isArray(selectedIds) ? selectedIds : [];
  const sSel = sel.map(String);
  if (snapIds.length === 0) return sSel.length === 0;
  return sSel.length === snapIds.length && sortJoin(sSel) === sortJoin(snapIds);
}

/**
 * After new filter-candidates: dimensions that were "all" (full match to snapshot) expand to
 * the new facet universe; other dimensions keep the user's exact selections (even if absent from the new lists).
 */
export function mergeFiltersAfterFilterCandidates(prev, snapshotFacets, nextFacets) {
  return {
    product_ids: selectionMatchesFullSnapshotIdNameOptions(prev.product_ids, snapshotFacets.titles)
      ? nextFacets.titles.map((t) => String(t.id))
      : [...prev.product_ids],
    collection_ids: selectionMatchesFullSnapshotIdNameOptions(prev.collection_ids, snapshotFacets.collections)
      ? (nextFacets.collections || []).map((c) => String(c.id))
      : [...(prev.collection_ids || [])],
    product_types: selectionMatchesFullSnapshotOptions(prev.product_types, snapshotFacets.product_types)
      ? [...nextFacets.product_types]
      : [...prev.product_types],
    tags: selectionMatchesFullSnapshotOptions(prev.tags, snapshotFacets.tags)
      ? [...nextFacets.tags]
      : [...prev.tags],
    sales_channels: selectionMatchesFullSnapshotOptions(prev.sales_channels, snapshotFacets.sales_channels)
      ? [...nextFacets.sales_channels]
      : [...prev.sales_channels],
    location_ids: selectionMatchesFullSnapshotIdNameOptions(prev.location_ids, snapshotFacets.locations)
      ? (nextFacets.locations || []).map((l) => String(l.id))
      : [...(prev.location_ids || [])],
    skus: selectionMatchesFullSnapshotOptions(
      prev.skus,
      prev.sku_mode === 'variant' ? snapshotFacets.skus : snapshotFacets.master_skus
    )
      ? [...(prev.sku_mode === 'variant' ? nextFacets.skus || [] : nextFacets.master_skus || [])]
      : [...(prev.skus || [])],
    sku_mode: prev.sku_mode === 'variant' ? 'variant' : 'master'
  };
}

/**
 * Build the filter portion of an analysis POST body. Returns native arrays/numbers — only
 * dimensions whose selection differs from the facet universe are included so that `{}` survives
 * "select all" and the request body stays compact. Numeric ids are sent as numbers (not strings).
 */
export function buildFilterQueryParams(facets, filters) {
  const p = {};
  const F = (x) => (Array.isArray(x) ? x : []);

  const cF = F(facets.collections);
  const cSnapIds = cF.map((c) => String(c.id));
  const cSel = F(filters.collection_ids);
  if (cSnapIds.length ? sortJoin(cSnapIds) !== sortJoin(cSel) : cSel.length > 0) {
    p.collection_ids = cSel.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0);
  }

  const ptF = F(facets.product_types);
  const ptSel = F(filters.product_types);
  if (ptF.length ? sortJoin(ptF) !== sortJoin(ptSel) : ptSel.length > 0) {
    p.product_type = [...ptSel];
  }

  const tagF = F(facets.tags);
  const tagSel = F(filters.tags);
  if (tagF.length ? sortJoin(tagF) !== sortJoin(tagSel) : tagSel.length > 0) {
    p.tags = [...tagSel];
  }

  const scF = F(facets.sales_channels);
  const scSel = F(filters.sales_channels);
  if (scF.length ? sortJoin(scF) !== sortJoin(scSel) : scSel.length > 0) {
    p.sales_channels = [...scSel];
  }

  const locIds = F(facets.locations).map((l) => String(l.id));
  const locSel = F(filters.location_ids).map(String);
  if (locIds.length ? sortJoin(locIds) !== sortJoin(locSel) : locSel.length > 0) {
    // Backend accepts numeric ids; send as Numbers to keep `$in` index lookups fast.
    p.location_ids = locSel.map((s) => Number(s)).filter((n) => Number.isFinite(n));
  }

  const titleIds = F(facets.titles).map((t) => String(t.id));
  const productIdSel = F(filters.product_ids).map(String);
  if (titleIds.length ? sortJoin(titleIds) !== sortJoin(productIdSel) : productIdSel.length > 0) {
    p.product_ids = productIdSel.map((s) => Number(s)).filter((n) => Number.isFinite(n));
  }

  const skuMode = filters.sku_mode === 'variant' ? 'variant' : 'master';
  const skuF = skuMode === 'master' ? F(facets.master_skus) : F(facets.skus);
  const skuSel = F(filters.skus);
  if (skuF.length ? sortJoin(skuF) !== sortJoin(skuSel) : skuSel.length > 0) {
    p.skus = [...skuSel];
    if (skuMode === 'variant') p.sku_mode = 'variant';
  }

  return p;
}

/**
 * Custom single-select dropdown styled to match the page's multi-select filters
 * (same trigger button + panel chrome). Used by the color radar control so the
 * dropdown visually matches the rest of the analysis filter row.
 */
export function SingleSelectDropdown({
  label,
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
  triggerClassName = '',
  // Optional node rendered on the far right of the first option row (stays open on click).
  firstOptionAccessory = null
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const fn = (e) => {
      if (!openRef.current) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const selected = options.find((o) => o.id === value) || options[0];
  const summary = selected ? selected.label : '—';

  return (
    <div className="product-analysis-filters__multiselect" ref={ref}>
      <button
        type="button"
        className={`product-analysis-filters__multiselect-trigger${triggerClassName ? ` ${triggerClassName}` : ''}`}
        aria-label={ariaLabel || label}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        {label ? `${label}: ${summary}` : summary}
      </button>
      {open && (
        <div
          className={`product-analysis-filters__multiselect-panel${
            firstOptionAccessory ? ' product-analysis-filters__multiselect-panel--with-accessory' : ''
          }`}
          role="listbox"
        >
          {options.map((opt, i) => {
            const isActive = opt.id === value;
            const showAccessory = i === 0 && firstOptionAccessory;
            return (
              <div
                key={opt.id ?? `__none-${i}`}
                className={`product-analysis-filters__multiselect-option${
                  showAccessory ? ' product-analysis-filters__multiselect-option--with-accessory' : ''
                }`}
                role="option"
                aria-selected={isActive}
              >
                <label
                  className="product-analysis-filters__multiselect-option-main"
                  style={isActive ? { fontWeight: 600 } : undefined}
                >
                  <input
                    type="radio"
                    name={`single-select-${label || 'value'}`}
                    checked={isActive}
                    onChange={() => {
                      if (!isActive) onChange(opt.id);
                      setOpen(false);
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
                {showAccessory ? (
                  <div
                    className="product-analysis-filters__multiselect-option-accessory"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {firstOptionAccessory}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Default-empty color performance payload. Mirrors the bucket shape returned by
 * the `getProductAnalysisData` endpoint so view-switching never has to handle
 * a missing key.
 */
const COLOR_DOMINANCE_VIEWS = [
  { id: 'all', label: 'All colors' },
  { id: 'high', label: 'High dominance' },
  { id: 'medium', label: 'Medium dominance' },
  { id: 'low', label: 'Low dominance' }
];
const EMPTY_COLOR_BUCKET = { total_attributed_revenue: 0, rows: [] };
const EMPTY_COLOR_PERFORMANCE = {
  all: EMPTY_COLOR_BUCKET,
  high: EMPTY_COLOR_BUCKET,
  medium: EMPTY_COLOR_BUCKET,
  low: EMPTY_COLOR_BUCKET
};

/**
 * Tag-performance treemap views. The two-view structure mirrors the bucketed
 * payload returned by the `/app/product-analysis` endpoint so view-switching
 * is instant and never triggers a refetch.
 */
const TAG_VIEWS = [
  { id: 'raw', label: 'Raw tags' },
  { id: 'single_words', label: 'Single words' }
];
const TAG_GROUP_IDS = ['subjects', 'themes', 'design_styles'];
const EMPTY_TAG_BUCKET = {
  groups: TAG_GROUP_IDS.map((id) => ({ id, rows: [] }))
};
const EMPTY_TAG_PERFORMANCE = {
  raw: EMPTY_TAG_BUCKET,
  single_words: EMPTY_TAG_BUCKET
};

/**
 * Normalize a `tag_performance` payload to the bucket shape used by the UI.
 * Tolerates a missing bucket key, missing `groups`, or out-of-order groups so
 * the UI keeps rendering during a deploy window where backend and frontend
 * shapes might briefly disagree.
 */
function normalizeTagPerformancePayload(input) {
  if (!input || typeof input !== 'object') return EMPTY_TAG_PERFORMANCE;
  const out = { ...EMPTY_TAG_PERFORMANCE };
  for (const { id: viewId } of TAG_VIEWS) {
    const bucket = input[viewId];
    if (!bucket || !Array.isArray(bucket.groups)) continue;
    const groupsById = new Map(bucket.groups.map((g) => [g?.id, g]));
    out[viewId] = {
      groups: TAG_GROUP_IDS.map((id) => {
        const g = groupsById.get(id);
        return {
          id,
          rows: Array.isArray(g?.rows)
            ? g.rows
                .map((r) => ({
                  name: String(r?.name || ''),
                  attributed_revenue: Number(r?.attributed_revenue || 0),
                  product_count: Number(r?.product_count || 0)
                }))
                .filter((r) => r.name && r.attributed_revenue > 0)
            : []
        };
      })
    };
  }
  return out;
}

/**
 * Normalize a `color_performance` payload from the API to the bucket shape used
 * by the UI. Accepts the new bucketed shape directly; tolerates the previous
 * single-shape payload (`{ total_attributed_revenue, rows }`) by collapsing it
 * into the `all` bucket so the UI keeps rendering during deploy windows.
 */
function normalizeColorPerformancePayload(input) {
  if (!input || typeof input !== 'object') return EMPTY_COLOR_PERFORMANCE;
  if (Array.isArray(input.rows)) {
    return {
      ...EMPTY_COLOR_PERFORMANCE,
      all: { total_attributed_revenue: Number(input.total_attributed_revenue || 0), rows: input.rows }
    };
  }
  const out = { ...EMPTY_COLOR_PERFORMANCE };
  for (const { id } of COLOR_DOMINANCE_VIEWS) {
    const b = input[id];
    if (b && Array.isArray(b.rows)) {
      out[id] = {
        total_attributed_revenue: Number(b.total_attributed_revenue || 0),
        rows: b.rows
      };
    }
  }
  return out;
}

/**
 * Radar chart that compares each color's actual revenue share against its
 * predicted dominance share (palette-weight-driven). See
 * examples/color_dominance_weight_calculation.md for the math behind the rows.
 *
 * - "Revenue share" — solid filled shape, points colored using each color's hex.
 * - "Dominance share" — dashed neutral-gray outline.
 * Where revenue extends beyond dominance the color is outperforming its palette
 * weight; where it falls inside, it's underperforming.
 */
function ColorPerformanceRadar({ rows, dollarFormat }) {
  const data = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => ({
      name: r.name,
      hex: r.hex || '#9CA3AF',
      revenue_pct: Math.max(0, Number(r.revenue_share || 0) * 100),
      dom_pct: Math.max(0, Number(r.dom_share || 0) * 100),
      performance_index: r.performance_index,
      total_revenue: Number(r.total_revenue || 0)
    }));
  }, [rows]);

  if (!data.length) {
    return (
      <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
        No color palette data available for this range.
      </div>
    );
  }

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    const idx = row.performance_index;
    const idxLabel =
      idx == null
        ? '—'
        : idx > 1.15
        ? `${idx.toFixed(2)} · Outperforming`
        : idx < 0.85
        ? `${idx.toFixed(2)} · Underperforming`
        : `${idx.toFixed(2)} · On target`;
    return (
      <div
        style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '8px 10px',
          fontSize: 12,
          minWidth: 180,
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}>
          <span
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 2,
              background: row.hex,
              border: '1px solid rgba(0,0,0,0.15)'
            }}
          />
          {row.name}
        </div>
        <div>Revenue share: {row.revenue_pct.toFixed(1)}%</div>
        <div>Dominance share: {row.dom_pct.toFixed(1)}%</div>
        <div>Performance index: {idxLabel}</div>
        <div>Attributed revenue: {dollarFormat.format(row.total_revenue)}</div>
      </div>
    );
  };

  /** Custom angle tick: paints the color name in the color's own hex (with a subtle
   * outline on very-light hexes so white-on-white is still legible). */
  const renderAngleTick = ({ x, y, payload, textAnchor }) => {
    const row = data.find((d) => d.name === payload.value);
    const hex = row?.hex || '#374151';
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor={textAnchor}
        fontSize={11}
        fontWeight={600}
        fill={hex}
        stroke="rgba(0,0,0,0.25)"
        strokeWidth={0.4}
        paintOrder="stroke"
      >
        {payload.value}
      </text>
    );
  };

  /** Filled colored dot per spoke for the actual-revenue radar so each spoke endpoint
   * carries its own color, eliminating the need for a separate legend. */
  const renderRevenueDot = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    return (
      <circle
        key={`${payload?.name}-${cx}-${cy}`}
        cx={cx}
        cy={cy}
        r={4}
        fill={payload?.hex || '#103E61'}
        stroke="white"
        strokeWidth={1.5}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={360}>
      <RadarChart data={data} margin={{ top: 12, right: 36, bottom: 12, left: 36 }}>
        <PolarGrid stroke="#e5e7eb" />
        <PolarAngleAxis dataKey="name" tick={renderAngleTick} />
        <PolarRadiusAxis tick={false} axisLine={false} />
        <Radar
          name="Dominance share"
          dataKey="dom_pct"
          stroke="#9CA3AF"
          strokeDasharray="4 4"
          fill="#9CA3AF"
          fillOpacity={0.08}
        />
        <Radar
          name="Revenue share"
          dataKey="revenue_pct"
          stroke="#103E61"
          strokeWidth={2}
          fill="#103E61"
          fillOpacity={0.18}
          dot={renderRevenueDot}
        />
        <RechartsTooltip content={renderTooltip} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

/**
 * Visual config for the tag-performance treemap. Each group gets a base hue;
 * per-tile fill darkens with `attributed_revenue / max_in_group` so the
 * dominant tile in each section reads as the darkest color.
 */
const TAG_GROUP_VISUALS = {
  subjects: { label: 'Subjects', baseHsl: { h: 209, s: 71 } }, // navy family (matches #103E61)
  themes: { label: 'Themes', baseHsl: { h: 36, s: 95 } }, // orange family (matches #F8A305)
  design_styles: { label: 'Design styles', baseHsl: { h: 280, s: 55 } }
};

/** HSL light → dark ramp used for treemap tile intensity. */
function tagTileFill(groupId, intensity) {
  const visual = TAG_GROUP_VISUALS[groupId] || TAG_GROUP_VISUALS.subjects;
  const t = Math.max(0, Math.min(1, intensity));
  // Lightness from 78% (faintest) down to 28% (darkest). Saturation stays put
  // so the hue stays recognizable across the ramp.
  const lightness = 78 - 50 * t;
  return `hsl(${visual.baseHsl.h}, ${visual.baseHsl.s}%, ${lightness}%)`;
}

/**
 * Custom recharts treemap tile content. Renders the colored rect, the tag/word
 * label centered inside (when there's enough room), and bumps the text fill to
 * white when the tile is dark enough that black would lose contrast.
 */
function TagTreemapContent(props) {
  // Recharts 2.x `Treemap` spreads each node's fields directly onto props
  // (it does NOT wrap them in a `payload` key). Only render leaves — the parent
  // group nodes (depth 1) are still drawn under the leaves and would otherwise
  // produce overlapping rects + duplicate labels.
  const { x, y, width, height, name, group, intensity, children } = props;
  const isLeaf = !Array.isArray(children) || children.length === 0;
  if (!isLeaf) return null;
  const i = Math.max(0, Math.min(1, Number(intensity || 0)));
  const fill = tagTileFill(group, i);
  const showLabel = width > 56 && height > 22;
  const textColor = i > 0.55 ? 'white' : '#1f2937';
  const label = String(name || '');
  const charBudget = Math.max(6, Math.floor(width / 7));
  const displayLabel =
    label.length > charBudget ? `${label.slice(0, Math.max(3, charBudget - 1))}…` : label;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke="white"
        strokeWidth={1}
      />
      {showLabel && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={12}
          fontWeight={600}
          fill={textColor}
          style={{ pointerEvents: 'none' }}
        >
          {displayLabel}
        </text>
      )}
    </g>
  );
}

/**
 * Side-by-side revenue treemap for `ai_attributes.subjects` (left) and
 * `ai_attributes.themes` and `ai_attributes.design_styles`. Tile area = attributed
 * revenue, tile darkness = revenue concentration within the group.
 */
function TagPerformanceTreemap({ groups, dollarFormat, numberFormat }) {
  const groupsById = useMemo(() => {
    const out = new Map();
    for (const g of groups || []) out.set(g.id, g);
    return out;
  }, [groups]);

  const data = useMemo(() => {
    const out = [];
    for (const id of TAG_GROUP_IDS) {
      const g = groupsById.get(id);
      const visual = TAG_GROUP_VISUALS[id] || TAG_GROUP_VISUALS.subjects;
      const rows = Array.isArray(g?.rows) ? g.rows.filter((r) => r.attributed_revenue > 0) : [];
      if (!rows.length) continue;
      const maxRevenue = rows.reduce((m, r) => Math.max(m, r.attributed_revenue), 0);
      out.push({
        name: visual.label,
        group: id,
        children: rows.map((r) => ({
          name: r.name,
          group: id,
          size: r.attributed_revenue,
          attributed_revenue: r.attributed_revenue,
          product_count: r.product_count,
          intensity: maxRevenue > 0 ? r.attributed_revenue / maxRevenue : 0
        }))
      });
    }
    return out;
  }, [groupsById]);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const tile = payload[0]?.payload;
    // The original data node we get back here doesn't carry a `depth` field —
    // detect leaves by the presence of attribution fields instead, and bail
    // out for the synthetic group/root nodes.
    if (!tile || tile.attributed_revenue == null) return null;
    const visual = TAG_GROUP_VISUALS[tile.group] || TAG_GROUP_VISUALS.subjects;
    return (
      <div
        style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '8px 10px',
          fontSize: 12,
          minWidth: 180,
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: tagTileFill(tile.group, 0.85)
            }}
          />
          <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {visual.label}
          </span>
        </div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{tile.name}</div>
        <div>Attributed revenue: {dollarFormat.format(tile.attributed_revenue)}</div>
        <div>
          Products: {numberFormat.format(tile.product_count)}
        </div>
      </div>
    );
  };

  if (!data.length) {
    return (
      <div
        style={{
          height: 360,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#888'
        }}
      >
        No tag data available for this range.
      </div>
    );
  }

  return (
    <div>
      {/* Inline legend matches the in-treemap fills so the user can tell the two
          groups apart even if one section is much smaller than the other. */}
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#374151', marginBottom: 6 }}>
        {TAG_GROUP_IDS.map((id) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2,
                background: tagTileFill(id, 0.85)
              }}
            />
            {TAG_GROUP_VISUALS[id].label}
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <Treemap
          data={data}
          dataKey="size"
          nameKey="name"
          stroke="white"
          isAnimationActive={false}
          content={<TagTreemapContent />}
        >
          <RechartsTooltip content={renderTooltip} />
        </Treemap>
      </ResponsiveContainer>
    </div>
  );
}

/** Stable string for effect deps — avoids re-fetch when facet arrays refresh but API params are unchanged. */
function stableAnalysisRequestKey(params) {
  if (!params) return null;
  const { start, end, timezone, ...rest } = params;
  const restKeys = Object.keys(rest).sort();
  const ordered = {
    start: start instanceof Date ? start.toISOString() : start,
    end: end instanceof Date ? end.toISOString() : end,
    timezone
  };
  for (const k of restKeys) {
    ordered[k] = rest[k];
  }
  return JSON.stringify(ordered);
}

function ProductAnalysisTableExportMenu({
  expandVariants,
  onExpandVariantsChange,
  exportBusy,
  onExport,
  disabled
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const handleExport = () => {
    onExport();
    setOpen(false);
  };

  return (
    <div className="product-analysis-table__actions" ref={rootRef}>
      <button
        type="button"
        className="product-analysis-table__actions-btn"
        aria-label="More"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="product-analysis-table__actions-hover-label" aria-hidden="true">
          More
        </span>
        <img src={ellipsisVerticalIcon} alt="" className="product-analysis-table__actions-icon" />
      </button>
      {open && (
        <div className="product-analysis-table__export-menu" role="menu">
          <div className="product-analysis-table__export-menu-toggle">
            <div className="product-analysis-table__export-menu-toggle-label">
              <span>Expand variants</span>
              <ToolTip
                className="product-analysis-table__export-menu-tip"
                message="When enabled, the CSV includes one row per variant with variant-level metrics instead of a single aggregated row per product."
                width={210}
              />
            </div>
            <label className="switch">
              <input
                className="switch-input"
                type="checkbox"
                checked={expandVariants}
                onChange={(e) => onExpandVariantsChange(e.target.checked)}
                disabled={exportBusy}
              />
              <span className="slider" />
            </label>
          </div>
          <button
            type="button"
            className="product-analysis-table__export-menu-action"
            role="menuitem"
            disabled={disabled || exportBusy}
            onClick={handleExport}
          >
            {exportBusy ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProductAnalysis() {
  const { getJwtToken, userProfile } = useContext(AuthContext);
  const timezone = userProfile?.store?.settings?.timezone;

  // View state lives in the URL so drilling into a product and coming back (or sharing the link)
  // restores the same filters. Parsed once on mount: hydration reads this ref, never `searchParams`,
  // so it can't fight the effect that writes the URL back.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStateRef = useRef(null);
  if (urlStateRef.current === null) urlStateRef.current = parseViewState(searchParams);
  // URL filters must be intersected with the facet universe before they can be sent, so only a
  // link that carries them has to wait for filter-candidates; everything else loads in parallel.
  const urlHadFiltersRef = useRef(null);
  if (urlHadFiltersRef.current === null) urlHadFiltersRef.current = !!urlStateRef.current.filters;

  const [activeRange, setActiveRange] = useState(DEFAULT_RANGE_LABEL);
  const start = new Date();
  start.setDate(start.getDate() - 7);
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const [match, setMatch] = useState({ start, end });
  const seededMatchForTz = useRef(null);
  const [matchReadyTz, setMatchReadyTz] = useState(null);

  const [salesOverTime, setSalesOverTime] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timeGrain, setTimeGrain] = useState('day');
  const [leftMetric, setLeftMetric] = useState(DEFAULT_LEFT_METRIC);
  const [rightMetric, setRightMetric] = useState(DEFAULT_RIGHT_METRIC);
  const [tagDistribution, setTagDistribution] = useState({ total_products: 0, rows: [] });
  const [variantSales, setVariantSales] = useState([]);
  const [colorPerformance, setColorPerformance] = useState(EMPTY_COLOR_PERFORMANCE);
  const [colorDominanceView, setColorDominanceView] = useState('all');
  const [tagPerformance, setTagPerformance] = useState(EMPTY_TAG_PERFORMANCE);
  const [tagPerformanceView, setTagPerformanceView] = useState('raw');
  const [tagSort, setTagSort] = useState({ sort_by: 'percent_of_revenue', sort_dir: -1 });
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [error, setError] = useState('');

  // Table state
  const [tableRows, setTableRows] = useState(false);
  const [tablePagination, setTablePagination] = useState(false);
  const [tableTotals, setTableTotals] = useState({ revenue: 0 });
  const [expanded, setExpanded] = useState({});
  // Variant rows load on expand: { [pid]: { status: 'loading' | 'ready' | 'error', rows } },
  // valid only for the analysis key in `variantsKeyRef`.
  const [variantsByPid, setVariantsByPid] = useState({});
  const variantsKeyRef = useRef(null);
  const [tableHoverImagePid, setTableHoverImagePid] = useState(null);
  const tableHoverImageLeaveTimerRef = useRef(null);
  const productAnalysisScrollRef = useRef(null);
  const productAnalysisTableBodyRef = useRef(null);
  const productTableImageAnchorByPidRef = useRef({});
  const [tableImagePopoverPos, setTableImagePopoverPos] = useState(null);
  const [tableSort, setTableSort] = useState(() => urlStateRef.current.sort || { sort_by: 'qty', sort_dir: -1 });
  const [tablePage, setTablePage] = useState(() => urlStateRef.current.page || 1);
  const [exportExpandVariants, setExportExpandVariants] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [facets, setFacets] = useState({
    // titles: [{ id: number, name: string }] — the dropdown displays `name`, the wire payload sends `id`.
    titles: [],
    collections: [],
    product_types: [],
    tags: [],
    sales_channels: [],
    skus: [],
    master_skus: []
  });
  const [filters, setFilters] = useState({
    product_ids: [],
    collection_ids: [],
    product_types: [],
    tags: [],
    sales_channels: [],
    skus: [],
    sku_mode: 'master'
  });
  const filtersSeededRef = useRef(false);
  const [filterFacetsReady, setFilterFacetsReady] = useState(false);
  const facetsRef = useRef(facets);
  facetsRef.current = facets;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const filtersSelectionKeyRef = useRef('');
  const matchKeyRef = useRef('');

  const numberFormat = useMemo(() => new Intl.NumberFormat('en-US'), []);
  const dollarFormat = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }),
    []
  );
  const compactDecimal = useMemo(
    () => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }),
    []
  );

  const lineData = useMemo(() => {
    if (!Array.isArray(salesOverTime)) return [];
    return salesOverTime;
  }, [salesOverTime]);

  const leftMetricOptions = useMemo(
    () =>
      SERIES_METRIC_OPTIONS.filter((o) => {
        if (o.id === rightMetric) return false;
        if (timeGrain === 'hour' && OPPORTUNITY_METRIC_IDS.has(o.id)) return false;
        return true;
      }),
    [rightMetric, timeGrain]
  );

  const rightMetricOptions = useMemo(
    () =>
      SERIES_METRIC_OPTIONS.filter((o) => {
        if (o.id === leftMetric) return false;
        if (timeGrain === 'hour' && OPPORTUNITY_METRIC_IDS.has(o.id)) return false;
        return true;
      }),
    [leftMetric, timeGrain]
  );

  const handleTimeGrainChange = (next) => {
    setTimeGrain(next);
    if (next === 'hour') {
      setLeftMetric((prev) => (OPPORTUNITY_METRIC_IDS.has(prev) ? DEFAULT_LEFT_METRIC : prev));
      setRightMetric((prev) => (OPPORTUNITY_METRIC_IDS.has(prev) ? DEFAULT_RIGHT_METRIC : prev));
    }
  };

  /**
   * Backend already rolls variant_sales up by title; map Revenue → Subtotal for PieGraph
   * and keep a client-side merge as a safety net if older payloads still have duplicates.
   */
  const variantPieData = useMemo(() => {
    if (!Array.isArray(variantSales) || variantSales.length === 0) return [];
    const byTitle = new Map();
    for (const row of variantSales) {
      const title = String(row?.name ?? 'Variant').trim() || 'Variant';
      const prev = byTitle.get(title) || { name: title, Subtotal: 0, Quantity: 0 };
      prev.Subtotal += Number(row?.Revenue ?? row?.Subtotal ?? 0) || 0;
      prev.Quantity += Number(row?.Quantity ?? 0) || 0;
      byTitle.set(title, prev);
    }
    const rows = [...byTitle.values()].sort(
      (a, b) => Number(b.Subtotal ?? 0) - Number(a.Subtotal ?? 0)
    );
    const totalUnits = rows.reduce((sum, r) => sum + (Number(r.Quantity) || 0), 0);
    return rows.map((r) => ({
      ...r,
      percentOfUnits: totalUnits > 0 ? (Number(r.Quantity) || 0) / totalUnits : 0
    }));
  }, [variantSales]);

  const sortedTagRows = useMemo(() => {
    const rows = Array.isArray(tagDistribution?.rows) ? [...tagDistribution.rows] : [];
    const dir = tagSort.sort_dir === 1 ? 1 : -1;
    const by = tagSort.sort_by;
    if (by === 'tag') {
      rows.sort((a, b) => String(a.tag || '').localeCompare(String(b.tag || ''), undefined, { sensitivity: 'base' }) * dir);
      return rows;
    }
    if (by === 'percent_of_products') {
      rows.sort((a, b) => (Number(a.percent_of_products || 0) - Number(b.percent_of_products || 0)) * dir);
      return rows;
    }
    // default: percent_of_revenue
    rows.sort((a, b) => (Number(a.percent_of_revenue || 0) - Number(b.percent_of_revenue || 0)) * dir);
    return rows;
  }, [tagDistribution, tagSort]);

  const filtersDisabled = !filterFacetsReady || loadingAnalysis || loadingTable;

  // An empty filter body means "everything", which is exactly what the all-selected facet seed
  // serializes to, so analytics and the table don't need to wait for filter-candidates.
  const analysisRequest = useMemo(() => {
    if (!timezone || matchReadyTz !== timezone) return { key: null, params: null };
    if (urlHadFiltersRef.current && !filterFacetsReady) return { key: null, params: null };
    const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    if (!filtersAllowAnalytics(facets, filters)) return { key: null, params: null };
    const params = {
      start: matchStart,
      end: matchEnd,
      timezone,
      time_grain: timeGrain,
      ...buildFilterQueryParams(facets, filters)
    };
    return { key: stableAnalysisRequestKey(params), params };
  }, [match, timezone, filters, facets, filterFacetsReady, matchReadyTz, timeGrain]);

  // Only reset facet filters when the store timezone changes (calendar semantics).
  useEffect(() => {
    if (!timezone) return;
    filtersSeededRef.current = false;
    setFilterFacetsReady(false);
    setFilters({
      product_ids: [],
      collection_ids: [],
      product_types: [],
      tags: [],
      sales_channels: [],
      skus: [],
      sku_mode: 'master'
    });
  }, [timezone]);

  useEffect(
    () => () => {
      if (tableHoverImageLeaveTimerRef.current) {
        clearTimeout(tableHoverImageLeaveTimerRef.current);
        tableHoverImageLeaveTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!loadingTable) return;
    if (tableHoverImageLeaveTimerRef.current) {
      clearTimeout(tableHoverImageLeaveTimerRef.current);
      tableHoverImageLeaveTimerRef.current = null;
    }
    setTableHoverImagePid(null);
  }, [loadingTable]);

  const hoveredProductTableRow = useMemo(() => {
    if (tableHoverImagePid == null || !Array.isArray(tableRows)) return null;
    return (
      tableRows.find((r) => String(r.shopify_product_id) === String(tableHoverImagePid)) ?? null
    );
  }, [tableHoverImagePid, tableRows]);

  useLayoutEffect(() => {
    if (tableHoverImagePid == null) {
      setTableImagePopoverPos(null);
      return;
    }
    const el = productTableImageAnchorByPidRef.current[String(tableHoverImagePid)];
    if (!el) {
      setTableImagePopoverPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const gap = 6;
    setTableImagePopoverPos({
      left: r.left + 8,
      bottom: window.innerHeight - r.top + gap
    });
  }, [tableHoverImagePid, expanded, tableRows, loadingTable]);

  useEffect(() => {
    if (tableHoverImagePid == null) return;
    const update = () => {
      const el = productTableImageAnchorByPidRef.current[String(tableHoverImagePid)];
      if (!el) {
        setTableImagePopoverPos(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      const gap = 6;
      setTableImagePopoverPos({
        left: rect.left + 8,
        bottom: window.innerHeight - rect.top + gap
      });
    };
    const bodyEl = productAnalysisTableBodyRef.current;
    const scrollEl = productAnalysisScrollRef.current;
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    bodyEl?.addEventListener('scroll', update, { passive: true });
    scrollEl?.addEventListener('scroll', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      bodyEl?.removeEventListener('scroll', update);
      scrollEl?.removeEventListener('scroll', update);
    };
  }, [tableHoverImagePid]);

  // Stable key for the user's *effective* filter selection (i.e. just the narrows that
  // actually become URL params). `buildFilterQueryParams` returns `{}` whenever a dimension's
  // selection equals its facet universe, so seeding `filters` with the full universe after the
  // first /filter-candidates response does NOT change this key and avoids a redundant second fetch.
  const filtersSelectionKey = useMemo(
    () => JSON.stringify(buildFilterQueryParams(facets, filters)),
    [facets, filters]
  );

  // Stable numeric key for the calendar range so re-creating Date instances with the same
  // calendar values (which `setMatch` does during seeding / preset clicks) doesn't refire.
  const matchKey = useMemo(
    () => `${match.start ? match.start.getTime() : ''}-${match.end ? match.end.getTime() : ''}`,
    [match]
  );
  filtersSelectionKeyRef.current = filtersSelectionKey;
  matchKeyRef.current = matchKey;

  // Mirror the view into the URL. `replace` keeps filter changes out of the history stack, so the
  // list keeps a single entry holding the latest view and Back from a product lands right on it.
  // Waits for the seed, otherwise the still-empty filter state would blow away incoming params.
  useEffect(() => {
    if (!timezone || !filterFacetsReady || !filtersSeededRef.current) return;
    const next = serializeViewState({
      narrowedFilters: buildFilterQueryParams(facets, filters),
      skuMode: filters.sku_mode,
      activeRange,
      match,
      timezone,
      tableSort,
      tablePage
    }).toString();
    if (next === window.location.search.replace(/^\?/, '')) return;
    setSearchParams(next, { replace: true });
    // `searchParams` is deliberately not a dependency: this effect writes the URL, hydration reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersSelectionKey,
    matchKey,
    activeRange,
    tableSort.sort_by,
    tableSort.sort_dir,
    tablePage,
    filterFacetsReady,
    timezone,
    setSearchParams
  ]);

  // Handed to the product detail page so its own back link returns to the filtered list, not the
  // bare route. The browser back button works regardless, since the filters live in the URL.
  const analyticsPathWithFilters = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/app/product-analysis/analytics?${query}` : '/app/product-analysis/analytics';
  }, [searchParams]);

  // Carry range + channel/location onto the product URL so detail opens with the same view context.
  const detailQuerySuffix = useMemo(() => {
    if (!timezone) return '';
    const q = serializeDetailViewState({
      narrowedFilters: buildFilterQueryParams(facets, filters),
      activeRange,
      match,
      timezone
    }).toString();
    return q ? `?${q}` : '';
  }, [facets, filters, activeRange, match, timezone]);

  // Filter-candidates on `match`: refresh facet option lists; per dimension, "all" (full snapshot) expands
  // to the new universe, narrow selections stay exactly as chosen (see mergeFiltersAfterFilterCandidates).
  useEffect(() => {
    if (!timezone || matchReadyTz !== timezone) return;
    const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    const capturedMatchKey = matchKey;
    const capturedFiltersKey = filtersSelectionKey;
    let cancelled = false;
    // Debounce only later refreshes (rapid filter clicks); the first load goes out immediately.
    const delayMs = filtersSeededRef.current ? 320 : 0;
    const t = setTimeout(async () => {
      try {
        const token = await getJwtToken();
        const base = { start: matchStart, end: matchEnd, timezone };
        const curFacets = facetsRef.current;
        const filtersNow = filtersRef.current;
        const filterParams = filtersSeededRef.current ? buildFilterQueryParams(curFacets, filtersNow) : {};
        const body = filtersSeededRef.current ? { ...base, ...filterParams } : base;
        const snapshotFacets = snapshotFacetLists(curFacets);
        // POST so large `product_ids` filter sets don't blow past API Gateway's URL limit.
        const res = await axios.post(
          'https://api.amplifyloyalty.com/app/product-analysis/filter-candidates',
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (
          capturedMatchKey !== matchKeyRef.current ||
          capturedFiltersKey !== filtersSelectionKeyRef.current
        ) {
          return;
        }
        const data = res.data || {};
        // Backend now returns `titles: [{id, title}]`. Normalize to `{id, name}` so the
        // generic `IdNameMultiSelectFilter` and the merge helpers can treat it like locations.
        const incomingTitles = Array.isArray(data.titles) ? data.titles : [];
        const titlesIdName = incomingTitles
          .map((t) => ({ id: Number(t?.id), name: String(t?.title ?? t?.name ?? '') }))
          .filter((t) => Number.isFinite(t.id) && t.id > 0);
        const incomingCollections = Array.isArray(data.collections) ? data.collections : [];
        const collectionsIdName = incomingCollections
          .map((c) => ({ id: Number(c?.id), name: String(c?.title ?? c?.name ?? '') }))
          .filter((c) => Number.isFinite(c.id) && c.id > 0);
        const nextFacets = {
          titles: titlesIdName,
          collections: collectionsIdName,
          product_types: data.product_types || [],
          tags: data.tags || [],
          sales_channels: Array.isArray(data.sales_channels) ? data.sales_channels : [],
          skus: Array.isArray(data.skus) ? data.skus : [],
          master_skus: Array.isArray(data.master_skus) ? data.master_skus : []
        };
        setFacets(nextFacets);
        setFilterFacetsReady(true);
        if (!filtersSeededRef.current) {
          filtersSeededRef.current = true;
          const seed = {
            product_ids: nextFacets.titles.map((t) => String(t.id)),
            collection_ids: nextFacets.collections.map((c) => String(c.id)),
            product_types: [...nextFacets.product_types],
            tags: [...nextFacets.tags],
            sales_channels: [...nextFacets.sales_channels],
            skus: [...nextFacets.master_skus],
            sku_mode: 'master'
          };
          // Narrow the seed to whatever the URL asked for, then drop it so later facet refreshes
          // go through the usual mergeFiltersAfterFilterCandidates path.
          const urlFilters = urlStateRef.current.filters;
          urlStateRef.current.filters = null;
          setFilters(applyUrlFiltersToSeed(seed, urlFilters, nextFacets));
        } else {
          setFilters((prev) => {
            const next = mergeFiltersAfterFilterCandidates(prev, snapshotFacets, nextFacets);
            try {
              if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
            } catch {
              /* ignore */
            }
            return next;
          });
        }
      } catch (err) {
        if (!cancelled) console.log(err);
      }
    }, delayMs);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey, timezone, filtersSelectionKey, matchReadyTz, getJwtToken]);

  // The initial `match` is browser-local; reseed it in the store calendar once the timezone is known.
  // A range in the URL wins, and named presets re-resolve so a shared link keeps its meaning.
  useEffect(() => {
    if (!timezone || seededMatchForTz.current === timezone) return;
    seededMatchForTz.current = timezone;
    const fromUrl = resolveRangeFromUrl(timezone, urlStateRef.current.range);
    if (fromUrl) {
      setMatch(fromUrl.match);
      setActiveRange(fromUrl.activeRange);
    } else {
      setMatch(storeMatchRangeLastNCalendarDays(timezone, 7));
      setActiveRange(DEFAULT_RANGE_LABEL);
    }
    setMatchReadyTz(timezone);
  }, [timezone]);

  useEffect(() => {
    const { key, params } = analysisRequest;
    if (!key || !params) {
      setLoadingAnalysis(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoadingAnalysis(true);
      setError('');
      try {
        const token = await getJwtToken();
        // POST keeps the request size unbounded; `top_products` was already removed because
        // the table view owns the paginated product rows.
        const res = await axios.post('https://api.amplifyloyalty.com/app/product-analysis', params, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;

        setSummary(res.data.summary ?? null);
        setTagDistribution(res.data.tag_distribution || { total_products: 0, rows: [] });
        setVariantSales(Array.isArray(res.data.variant_sales) ? res.data.variant_sales : []);
        setColorPerformance(normalizeColorPerformancePayload(res.data.color_performance));
        setTagPerformance(normalizeTagPerformancePayload(res.data.tag_performance));
        const raw = Array.isArray(res.data.sales_over_time) ? res.data.sales_over_time : [];
        const emptySeries = EMPTY_SERIES_POINT;
        const grain = String(params.time_grain || 'day');
        let filled = raw;
        if (grain === 'day') {
          filled = await fillDates(raw, match.start, match.end, emptySeries, timezone);
        } else if (grain === 'month') {
          filled = fillMonths(raw, match.start, match.end, emptySeries, timezone);
        }
        if (!cancelled) setSalesOverTime(filled);
      } catch (err) {
        if (cancelled) return;
        console.log(err);
        setError('Failed to load product analysis.');
        setSummary(null);
        setTagDistribution({ total_products: 0, rows: [] });
        setVariantSales([]);
        setSalesOverTime([]);
        setColorPerformance(EMPTY_COLOR_PERFORMANCE);
        setTagPerformance(EMPTY_TAG_PERFORMANCE);
      } finally {
        if (!cancelled) setLoadingAnalysis(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // `analysisRequest.key` is a stable JSON string that already encodes start/end/timezone/filters,
    // so the previously-listed match.start / match.end / timezone deps were redundant Date refs that
    // changed on every `setMatch` call (even when the resulting time was identical) and forced a
    // duplicate fetch. `match` itself is read inside the effect for `fillDates` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisRequest.key, getJwtToken]);

  useEffect(() => {
    const { key, params } = analysisRequest;
    if (!key || !params) {
      setLoadingTable(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoadingTable(true);
      try {
        const token = await getJwtToken();
        const tableQuery = {
          ...params,
          page: tablePage,
          pageSize: 50,
          sort_by: tableSort.sort_by,
          sort_dir: tableSort.sort_dir
        };
        const res = await axios.post(
          'https://api.amplifyloyalty.com/app/product-analysis/table',
          tableQuery,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;

        const pageInfo = res.data.page_info || {};
        setTablePagination({
          page: pageInfo.page || 1,
          limit: pageInfo.page_size || 50,
          totalDocs: pageInfo.total_docs || 0,
          totalPages: pageInfo.total_pages || 1,
          hasNextPage: !!pageInfo.has_next_page,
          hasPrevPage: !!pageInfo.has_prev_page,
          nextPage: pageInfo.next_page,
          prevPage: pageInfo.prev_page
        });
        setTableTotals(res.data.totals || { revenue: 0 });
        setTableRows(res.data.rows || []);
      } catch (err) {
        if (!cancelled) {
          console.log(err);
          setTableRows([]);
        }
      } finally {
        if (!cancelled) setLoadingTable(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [analysisRequest.key, tablePage, tableSort.sort_by, tableSort.sort_dir, getJwtToken]);

  useEffect(() => {
    variantsKeyRef.current = analysisRequest.key;
    setVariantsByPid({});
  }, [analysisRequest.key]);

  useEffect(() => {
    const { key, params } = analysisRequest;
    if (!key || !params || loadingTable || !Array.isArray(tableRows)) return;
    if (variantsKeyRef.current !== key) return;
    const missing = tableRows
      .map((r) => r.shopify_product_id)
      .filter((pid) => expanded[pid] && !variantsByPid[pid]);
    if (!missing.length) return;
    setVariantsByPid((prev) => {
      const next = { ...prev };
      for (const pid of missing) next[pid] = { status: 'loading', rows: [] };
      return next;
    });
    const totalRevenue = tableTotals.revenue || 0;
    missing.forEach(async (pid) => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          'https://api.amplifyloyalty.com/app/product-analysis/table/variants',
          { ...params, shopify_product_id: pid, total_revenue: totalRevenue },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (variantsKeyRef.current !== key) return;
        const rows = Array.isArray(res.data?.variants) ? res.data.variants : [];
        setVariantsByPid((prev) => ({ ...prev, [pid]: { status: 'ready', rows } }));
      } catch (err) {
        console.log(err);
        if (variantsKeyRef.current !== key) return;
        setVariantsByPid((prev) => ({ ...prev, [pid]: { status: 'error', rows: [] } }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisRequest.key, expanded, tableRows, loadingTable, variantsByPid, getJwtToken]);

  const toggleExpanded = (pid) => {
    // Re-expanding a row whose variant request failed retries it.
    setVariantsByPid((prev) => {
      if (prev[pid]?.status !== 'error') return prev;
      const { [pid]: _failed, ...rest } = prev;
      return rest;
    });
    setExpanded((prev) => ({ ...prev, [pid]: !prev[pid] }));
  };

  const patchFilters = (patch) => {
    setTablePage(1);
    setFilters((p) => ({ ...p, ...patch }));
  };

  const exportTableCsv = async () => {
    const { params } = analysisRequest;
    if (!params || exportBusy) return;
    setExportBusy(true);
    setError('');
    try {
      const token = await getJwtToken();
      const exportQuery = {
        ...params,
        sort_by: tableSort.sort_by,
        sort_dir: tableSort.sort_dir,
        expand_variants: exportExpandVariants
      };
      const res = await axios.post(
        'https://api.amplifyloyalty.com/app/product-analysis/table/export',
        exportQuery,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const csv = res.data?.csv;
      const filename = res.data?.filename || 'product-analysis.csv';
      if (!csv) throw new Error('No export data returned.');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || err.message || 'Export failed.');
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <div className="product-analysis-scroll" ref={productAnalysisScrollRef}>
      <div className="dashboard dashboard--product-analysis">
      <div className="dashboard__filters product-analysis-filters product-analysis-filters--split-rows">
        <div className="product-analysis-filters__stack">
          <div className="product-analysis-filters__left">
            <AnalyticsSourceSelector
              current="shopify"
              activeRange={activeRange}
              match={match}
              timezone={timezone}
            />
            <div className="date-filter promotions-table-filters__filter-wrapper">
              <StoreDateRangeFilter
                active={activeRange}
                setActive={setActiveRange}
                timezone={timezone}
                match={match}
                disabled={filtersDisabled}
                popoverAlign="start"
                options={[
                  ...RANGE_PRESETS.map((preset) => ({
                    text: preset.label,
                    action: () => setMatch(preset.resolve(timezone))
                  })),
                  {
                    text: CUSTOM_RANGE_LABEL,
                    action: (customStart, customEnd) => setMatch({ start: customStart, end: customEnd })
                  }
                ]}
              />
            </div>
          </div>
          <div className="product-analysis-filters__filter-panel">
            <IdNameMultiSelectFilter
              label="Product title"
              options={facets.titles}
              value={filters.product_ids}
              onChange={(product_ids) => patchFilters({ product_ids })}
              showSearch
              searchPlaceholder="Search titles"
              emptyText="No products in range"
              disabled={filtersDisabled}
            />
            <IdNameMultiSelectFilter
              label="Collections"
              options={facets.collections}
              value={filters.collection_ids}
              onChange={(collection_ids) => patchFilters({ collection_ids })}
              showSearch
              searchPlaceholder="Search collections"
              emptyText="No collections in range"
              disabled={filtersDisabled}
            />
            <MultiSelectFilter
              label="Product types"
              options={facets.product_types}
              value={filters.product_types}
              onChange={(product_types) => patchFilters({ product_types })}
              showSearch
              searchPlaceholder="Search product types"
              disabled={filtersDisabled}
            />
            <MultiSelectFilter
              label="Tags"
              options={facets.tags}
              value={filters.tags}
              onChange={(tags) => patchFilters({ tags })}
              showSearch
              searchPlaceholder="Search tags"
              disabled={filtersDisabled}
            />
            <MultiSelectFilter
              label="Sales channel"
              options={facets.sales_channels}
              value={filters.sales_channels}
              onChange={(sales_channels) => patchFilters({ sales_channels })}
              showSearch
              searchPlaceholder="Search sales channels"
              disabled={filtersDisabled}
            />
            <SkuMultiSelectFilter
              variantOptions={facets.skus}
              masterOptions={facets.master_skus}
              skuMode={filters.sku_mode === 'variant' ? 'variant' : 'master'}
              value={filters.skus}
              onChange={(skus) => patchFilters({ skus })}
              onModeChange={(sku_mode) => patchFilters({ sku_mode, skus: [] })}
              disabled={filtersDisabled}
            />
          </div>
        </div>
      </div>

      {error && <div className="float-element" style={{ padding: 12 }}>{error}</div>}

      <div
        className="dashboard__row"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'stretch',
          gap: 16,
          width: '100%',
          flexWrap: 'nowrap'
        }}
      >
        <div
          className="float-element product-analysis-scorecards-wrap"
          style={{
            width: '33%',
            maxWidth: '415px',
            flexShrink: 0,
            boxSizing: 'border-box',
            padding: '20px'
          }}
        >
          <div className="product-analysis-scorecards">
            <Scorecard
              className="dashboard__scorecard"
              title="Net Sales"
              data={
                loadingAnalysis || !summary ? '—' : dollarFormat.format(summary.net_sales ?? 0)
              }
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Avg. Daily Sales"
              data={
                loadingAnalysis || !summary
                  ? '—'
                  : dollarFormat.format(summary.avg_daily_sales ?? 0)
              }
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Orders"
              data={loadingAnalysis || !summary ? '—' : numberFormat.format(Math.round(summary.order_count ?? 0))}
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Avg. Daily Orders"
              data={
                loadingAnalysis || !summary
                  ? '—'
                  : compactDecimal.format(summary.avg_daily_orders ?? 0)
              }
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Units Sold"
              data={loadingAnalysis || !summary ? '—' : numberFormat.format(Math.round(summary.units_sold ?? 0))}
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Avg. Sale Price"
              data={loadingAnalysis || !summary ? '—' : dollarFormat.format(summary.aov ?? 0)}
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Refunds"
              data={
                loadingAnalysis || !summary ? '—' : dollarFormat.format(summary.refunds ?? 0)
              }
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Returning Cust. %"
              data={
                loadingAnalysis || !summary
                  ? '—'
                  : `${Math.round((summary.returning_customer_rate ?? 0) * 1000) / 10}%`
              }
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Opportunity Cost"
              data={
                loadingAnalysis || !summary
                  ? '—'
                  : dollarFormat.format(summary.opportunity_cost ?? 0)
              }
            />
            <Scorecard
              className="dashboard__scorecard"
              title="Lost Units"
              data={
                loadingAnalysis || !summary
                  ? '—'
                  : numberFormat.format(Math.round((summary.lost_units ?? 0) * 10) / 10)
              }
            />
          </div>
        </div>
        <div
          style={{
            flex: '1 1 0',
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
            alignSelf: 'stretch'
          }}
        >
          <LineGraph
            width="100%"
            height="100%"
            data={lineData}
            heading={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  width: '100%'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    minWidth: 0
                  }}
                >
                  <span>Sales over Time</span>
                  <SingleSelectDropdown
                    ariaLabel="Left series"
                    options={leftMetricOptions}
                    value={leftMetric}
                    onChange={setLeftMetric}
                    disabled={loadingAnalysis}
                    triggerClassName="product-analysis-filters__multiselect-trigger--series-left"
                  />
                  <span aria-hidden="true">x</span>
                  <SingleSelectDropdown
                    ariaLabel="Right series"
                    options={rightMetricOptions}
                    value={rightMetric}
                    onChange={setRightMetric}
                    disabled={loadingAnalysis}
                    triggerClassName="product-analysis-filters__multiselect-trigger--series-right"
                  />
                </div>
                <SingleSelectDropdown
                  label="Time"
                  ariaLabel="Time dimension"
                  options={TIME_GRAIN_OPTIONS}
                  value={timeGrain}
                  onChange={handleTimeGrainChange}
                  disabled={loadingAnalysis}
                  triggerClassName="product-analysis-filters__multiselect-trigger--time"
                />
              </div>
            }
            className="float-element product-analysis-line-graph"
            showLegend={false}
            keys={[leftMetric, rightMetric]}
            keyLabels={{
              [leftMetric]: SERIES_METRICS[leftMetric]?.label || leftMetric,
              [rightMetric]: SERIES_METRICS[rightMetric]?.label || rightMetric
            }}
            formatter={(value, name) => {
              const leftLabel = SERIES_METRICS[leftMetric]?.label;
              const metricId = name === leftLabel ? leftMetric : rightMetric;
              return formatSeriesTooltip(metricId, value, dollarFormat, numberFormat);
            }}
            yLeftTickFormatter={(v) => formatSeriesTick(leftMetric, v)}
            yRightTickFormatter={(v) => formatSeriesTick(rightMetric, v)}
            tickFormatter={(value) => formatTimeGrainTick(value, timeGrain)}
            labelFormatter={(value) => formatTimeGrainLabel(value, timeGrain)}
          />
        </div>
      </div>

      <section
        className="product-analysis-table__container"
        style={{
          position: 'relative',
          width: '100%',
          marginTop: 20,
          alignSelf: 'stretch',
          boxSizing: 'border-box'
        }}
      >
        <div className="product-analysis-table">
          <ProductAnalysisTableExportMenu
            expandVariants={exportExpandVariants}
            onExpandVariantsChange={setExportExpandVariants}
            exportBusy={exportBusy}
            onExport={exportTableCsv}
            disabled={!analysisRequest.params || loadingTable}
          />
          <div
            className="product-analysis-table__table-container product-analysis-table__table-container--frozen"
            ref={productAnalysisTableBodyRef}
          >
            <div className="product-analysis-table__header">
              <div className="product-analysis-table__heading product-analysis-table__heading--product">
                <div className="product-analysis-table__heading-text">Product</div>
              </div>
              <div className="product-analysis-table__heading product-analysis-table__heading--sku">
                <div className="product-analysis-table__heading-text">SKU</div>
              </div>
              <div className="product-analysis-table__metrics">
                <div
                  className={`product-analysis-table__heading product-analysis-table__sort-heading${tableSort.sort_by === 'qty' ? ' product-analysis-table__sort-heading--active' : ''}`}
                  onClick={() =>
                    setTableSort((s) => ({
                      sort_by: 'qty',
                      sort_dir: s.sort_by === 'qty' ? s.sort_dir * -1 : -1
                    }))
                  }
                >
                  <div className="product-analysis-table__heading-text">Qty Sold</div>
                  <Caret className="product-analysis-table__heading-caret" direction={tableSort.sort_by === 'qty' ? tableSort.sort_dir : -1} />
                </div>
                <div
                  className={`product-analysis-table__heading product-analysis-table__sort-heading${tableSort.sort_by === 'revenue' ? ' product-analysis-table__sort-heading--active' : ''}`}
                  onClick={() =>
                    setTableSort((s) => ({
                      sort_by: 'revenue',
                      sort_dir: s.sort_by === 'revenue' ? s.sort_dir * -1 : -1
                    }))
                  }
                >
                  <div className="product-analysis-table__heading-text">Revenue</div>
                  <Caret className="product-analysis-table__heading-caret" direction={tableSort.sort_by === 'revenue' ? tableSort.sort_dir : -1} />
                </div>
                <div className="product-analysis-table__heading">
                  <div className="product-analysis-table__heading-text">Avg Daily</div>
                </div>
                <div
                  className={`product-analysis-table__heading product-analysis-table__sort-heading${tableSort.sort_by === 'opportunity' ? ' product-analysis-table__sort-heading--active' : ''}`}
                  onClick={() =>
                    setTableSort((s) => ({
                      sort_by: 'opportunity',
                      sort_dir: s.sort_by === 'opportunity' ? s.sort_dir * -1 : -1
                    }))
                  }
                >
                  <div className="product-analysis-table__heading-text">Opp. Cost</div>
                  <Caret className="product-analysis-table__heading-caret" direction={tableSort.sort_by === 'opportunity' ? tableSort.sort_dir : -1} />
                </div>
                <div
                  className={`product-analysis-table__heading product-analysis-table__sort-heading${tableSort.sort_by === 'lost' ? ' product-analysis-table__sort-heading--active' : ''}`}
                  onClick={() =>
                    setTableSort((s) => ({
                      sort_by: 'lost',
                      sort_dir: s.sort_by === 'lost' ? s.sort_dir * -1 : -1
                    }))
                  }
                >
                  <div className="product-analysis-table__heading-text">Lost Units</div>
                  <Caret className="product-analysis-table__heading-caret" direction={tableSort.sort_by === 'lost' ? tableSort.sort_dir : -1} />
                </div>
                <div className="product-analysis-table__heading">
                  <div className="product-analysis-table__heading-text">% of Sales</div>
                </div>
                <div
                  className={`product-analysis-table__heading product-analysis-table__sort-heading${tableSort.sort_by === 'sellout' ? ' product-analysis-table__sort-heading--active' : ''}`}
                  onClick={() =>
                    setTableSort((s) => ({
                      sort_by: 'sellout',
                      sort_dir: s.sort_by === 'sellout' ? s.sort_dir * -1 : 1
                    }))
                  }
                >
                  <div className="product-analysis-table__heading-text">Est. Sell Out</div>
                  <Caret className="product-analysis-table__heading-caret" direction={tableSort.sort_by === 'sellout' ? tableSort.sort_dir : -1} />
                </div>
                <div className="product-analysis-table__heading">
                  <div className="product-analysis-table__heading-text">Inventory</div>
                </div>
              </div>
            </div>

            <div className="product-analysis-table__body">
              {tableRows === false || loadingTable ? (
                <div className="product-analysis-table__center-state">
                  <img className="product-analysis-table__loading-spinner" src={loadingBuffering} alt="Loading products" />
                </div>
              ) : Array.isArray(tableRows) && tableRows.length === 0 ? (
                <div className="product-analysis-table__center-state product-analysis-table__center-state--empty">
                  No results found based on current filters.
                </div>
              ) : (
                Array.isArray(tableRows) &&
                tableRows.map((row) => {
                  const pid = row.shopify_product_id;
                  const canExpand = row.has_variants !== false;
                  const isOpen = canExpand && !!expanded[pid];
                  const variantEntry = variantsByPid[pid];
                  return (
                    <div
                      key={pid}
                      data-product-table-group={pid}
                      style={{ width: '100%' }}
                      onMouseEnter={() => {
                        if (tableHoverImageLeaveTimerRef.current) {
                          clearTimeout(tableHoverImageLeaveTimerRef.current);
                          tableHoverImageLeaveTimerRef.current = null;
                        }
                        setTableHoverImagePid(pid);
                      }}
                      onMouseLeave={() => {
                        if (tableHoverImageLeaveTimerRef.current) {
                          clearTimeout(tableHoverImageLeaveTimerRef.current);
                        }
                        tableHoverImageLeaveTimerRef.current = setTimeout(() => {
                          setTableHoverImagePid(null);
                          tableHoverImageLeaveTimerRef.current = null;
                        }, 120);
                      }}
                    >
                      <div
                        ref={(el) => {
                          const key = String(pid);
                          if (el) productTableImageAnchorByPidRef.current[key] = el;
                          else delete productTableImageAnchorByPidRef.current[key];
                        }}
                        className="product-analysis-table__row"
                        style={{ position: 'relative' }}
                      >
                        <div
                          className={`product-analysis-table__cell${canExpand ? ' product-analysis-table__cell--link' : ''}`}
                          onClick={canExpand ? () => toggleExpanded(pid) : undefined}
                          style={canExpand ? { cursor: 'pointer' } : undefined}
                        >
                          <Link
                            to={`/app/product-analysis/analytics/product/${pid}${detailQuerySuffix}`}
                            state={{ from: analyticsPathWithFilters, fromLabel: 'Analytics' }}
                            className="product-analysis-table__drill"
                            title="Open product detail"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View
                          </Link>
                          <span className="product-analysis-table__expander">{canExpand ? (isOpen ? '▾' : '▸') : ''}</span>
                          {row.title || row.handle || pid}
                        </div>
                        <div
                          className="product-analysis-table__cell product-analysis-table__cell--sku"
                          title={row.master_sku || ''}
                        >
                          {row.master_sku || '—'}
                        </div>
                        <div className="product-analysis-table__metrics">
                          <div className="product-analysis-table__cell">{numberFormat.format(Math.round(row.qty_sold || 0))}</div>
                          <div className="product-analysis-table__cell">{dollarFormat.format(row.revenue || 0)}</div>
                          <div className="product-analysis-table__cell">
                            {numberFormat.format(Math.round((row.avg_daily_qty || 0) * 10) / 10)}
                          </div>
                          <div className="product-analysis-table__cell">{dollarFormat.format(row.opportunity_cost || 0)}</div>
                          <div className="product-analysis-table__cell">
                            {numberFormat.format(Math.round((row.lost_units || 0) * 10) / 10)}
                          </div>
                          <div className="product-analysis-table__cell">
                            {tableTotals.revenue > 0 ? `${Math.round((row.percent_of_total_sales || 0) * 1000) / 10}%` : '0%'}
                          </div>
                          <div className="product-analysis-table__cell">
                            {row.estimated_sellout_date ? new Date(row.estimated_sellout_date).toLocaleDateString() : '—'}
                          </div>
                          <div className="product-analysis-table__cell">{numberFormat.format(Math.round(row.inventory_total || 0))}</div>
                        </div>
                      </div>

                      {isOpen && (!variantEntry || variantEntry.status !== 'ready') && (
                        <div
                          className="product-analysis-table__row product-analysis-table__row--variant"
                          style={{ position: 'relative' }}
                        >
                          <div className="product-analysis-table__cell" style={{ paddingLeft: 28 }}>
                            {variantEntry?.status === 'error' ? 'Failed to load variants.' : 'Loading variants…'}
                          </div>
                        </div>
                      )}
                      {isOpen &&
                        variantEntry?.status === 'ready' &&
                        variantEntry.rows.map((v) => {
                          const key = `${pid}-${v.shopify_variant_id || v.sku || v.variant_title}`;
                          return (
                            <div
                              key={key}
                              className="product-analysis-table__row product-analysis-table__row--variant"
                              style={{ position: 'relative' }}
                            >
                              <div
                                className="product-analysis-table__cell"
                                style={{ paddingLeft: 28 }}
                                title={v.variant_title || ''}
                              >
                                {v.variant_title || v.shopify_variant_id || 'Variant'}
                              </div>
                              <div className="product-analysis-table__cell product-analysis-table__cell--sku" title={v.sku || ''}>
                                {v.sku || '—'}
                              </div>
                              <div className="product-analysis-table__metrics">
                                <div className="product-analysis-table__cell">{numberFormat.format(Math.round(v.qty_sold || 0))}</div>
                                <div className="product-analysis-table__cell">{dollarFormat.format(v.revenue || 0)}</div>
                                <div className="product-analysis-table__cell">
                                  {numberFormat.format(Math.round((v.avg_daily_qty || 0) * 10) / 10)}
                                </div>
                                <div className="product-analysis-table__cell">{dollarFormat.format(v.opportunity_cost || 0)}</div>
                                <div className="product-analysis-table__cell">
                                  {numberFormat.format(Math.round((v.lost_units || 0) * 10) / 10)}
                                </div>
                                <div className="product-analysis-table__cell">
                                  {tableTotals.revenue > 0
                                    ? `${Math.round((v.percent_of_total_sales || 0) * 1000) / 10}%`
                                    : '0%'}
                                </div>
                                <div className="product-analysis-table__cell">
                                  {v.estimated_sellout_date ? new Date(v.estimated_sellout_date).toLocaleDateString() : '—'}
                                </div>
                                <div className="product-analysis-table__cell">{numberFormat.format(Math.round(v.inventory_quantity || 0))}</div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {tablePagination && (
            <TablePagination
              options={tablePagination}
              docType="products"
              previous={() => setTablePage(tablePagination.prevPage)}
              next={() => setTablePage(tablePagination.nextPage)}
            />
          )}
        </div>
      </section>
        
      <div className="dashboard__row" style={{ width: '100%', gap: 16, marginTop: 20 }}>
      <div className="float-element" style={{ flex: 1, boxSizing: 'border-box', padding: 16, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            <div className="line-graph__heading" style={{ marginBottom: 0 }}>
              Tag Performance
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ToolTip
                width="260px"
                message={
                  <div style={{ textAlign: 'left', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Tag views</div>
                    <div style={{ marginBottom: 4 }}>
                      <strong>Raw tags</strong> — multi-word phrases as extracted from the product
                      image analysis.
                    </div>
                    <div>
                      <strong>Single words</strong> — phrases split into individual words with
                      common stop-words and structural words filtered out, surfacing broader themes.
                    </div>
                  </div>
                }
              />
              <SingleSelectDropdown
                label="View"
                ariaLabel="Tag performance view"
                options={TAG_VIEWS.map((v) => ({ id: v.id, label: v.label }))}
                value={tagPerformanceView}
                onChange={setTagPerformanceView}
              />
            </div>
          </div>
          <TagPerformanceTreemap
            groups={(tagPerformance[tagPerformanceView] || EMPTY_TAG_BUCKET).groups || []}
            dollarFormat={dollarFormat}
            numberFormat={numberFormat}
          />
        </div>

        <div className="float-element" style={{ width: '40%', boxSizing: 'border-box', padding: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            <div className="line-graph__heading" style={{ marginBottom: 0 }}>
              Color Performance
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ToolTip
                width="240px"
                message={
                  <div style={{ textAlign: 'left', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Dominance views</div>
                    <div style={{ marginBottom: 4 }}>
                      <strong>All colors</strong> — every color in every product palette.
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <strong>High</strong> — the color most present in the product.
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <strong>Medium</strong> — secondary palette colors that support the design
                      but aren't the focal point.
                    </div>
                    <div>
                      <strong>Low</strong> — minor accent colors used sparingly in the design.
                    </div>
                  </div>
                }
              />
              <SingleSelectDropdown
                label="View"
                ariaLabel="Color dominance view"
                options={COLOR_DOMINANCE_VIEWS.map((v) => ({ id: v.id, label: v.label }))}
                value={colorDominanceView}
                onChange={setColorDominanceView}
              />
            </div>
          </div>
          <ColorPerformanceRadar
            rows={(colorPerformance[colorDominanceView] || EMPTY_COLOR_BUCKET).rows || []}
            dollarFormat={dollarFormat}
          />
        </div>
      </div>

      <div className="dashboard__row" style={{ width: '100%', gap: 16, marginTop: 20 }}>
        <div className="float-element" style={{ width: '40%', boxSizing: 'border-box' }}>
          <PieGraph
            heading="Sales by Variant Title"
            data={variantPieData || []}
            width="100%"
            height={320}
            value="Subtotal"
            name="name"
            fill={['#103E61', '#F8A305', '#F51504', '#2A9D8F', '#6D28D9', '#111827', '#9CA3AF']}
            formatter={dollarFormat}
            outerRadius={105}
            legendLayout="horizontal"
            legendVerticalAlign="bottom"
            legendAlign="center"
            legendMaxItems={20}
            renderTooltip={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload || {};
              if (row.name === 'No sales') return null;
              const units = Math.round(Number(row.Quantity) || 0);
              const pct = Math.round((Number(row.percentOfUnits) || 0) * 1000) / 10;
              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #ccc',
                    padding: '8px 10px',
                    fontSize: 12,
                    lineHeight: 1.45
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{row.name}</div>
                  <div>{dollarFormat.format(Number(row.Subtotal) || 0)}</div>
                  <div>
                    {numberFormat.format(units)} units ({pct}%)
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="float-element" style={{ flex: 1, boxSizing: 'border-box', padding: 16, minWidth: 0 }}>
          {/* <div className="line-graph__heading">Tag Share of Products</div> */}
          <div className="basic-table__header">
            <div
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onClick={(e) => {
                e.stopPropagation();
                setTagSort((s) => ({
                  sort_by: 'tag',
                  sort_dir: s.sort_by === 'tag' ? s.sort_dir * -1 : 1
                }));
              }}
            >
              Product Tag
              <Caret direction={tagSort.sort_by === 'tag' ? -tagSort.sort_dir : -1} className="product-analysis-table__heading-caret" />
            </div>
            <div
              style={{
                width: 90,
                marginRight: 10,
                textAlign: 'right',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setTagSort((s) => ({
                  sort_by: 'percent_of_revenue',
                  sort_dir: s.sort_by === 'percent_of_revenue' ? s.sort_dir * -1 : -1
                }));
              }}
            >
              Sales
              <Caret
                direction={tagSort.sort_by === 'percent_of_revenue' ? tagSort.sort_dir : -1}
                className="product-analysis-table__heading-caret"
              />
            </div>
            <div
              style={{
                width: 90,
                marginRight: 10,
                textAlign: 'right',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setTagSort((s) => ({
                  sort_by: 'percent_of_products',
                  sort_dir: s.sort_by === 'percent_of_products' ? s.sort_dir * -1 : -1
                }));
              }}
            >
              Products
              <Caret
                direction={tagSort.sort_by === 'percent_of_products' ? tagSort.sort_dir : -1}
                className="product-analysis-table__heading-caret"
              />
            </div>
            <div style={{ width: 80, textAlign: 'right' }}>Total</div>
          </div>
          <div className="basic-table__body" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {(sortedTagRows || []).length === 0 && (
              <div className="basic-table__row">
                <div style={{ color: 'var(--border-gray)' }}>No tags found.</div>
              </div>
            )}
            {(sortedTagRows || []).map((r) => (
              <div key={r.tag} className="basic-table__row">
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.tag}</div>
                <div style={{ width: 90, marginRight: 10, textAlign: 'right' }}>
                  {`${Math.round((r.percent_of_revenue || 0) * 1000) / 10}%`}
                </div>
                <div style={{ width: 90, marginRight: 10, textAlign: 'right' }}>
                  {`${Math.round((r.percent_of_products || 0) * 1000) / 10}%`}
                </div>
                <div style={{ width: 80, textAlign: 'right' }}>
                  {numberFormat.format(Math.round(r.product_count || 0))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {typeof document !== 'undefined' &&
        tableHoverImagePid != null &&
        tableImagePopoverPos &&
        hoveredProductTableRow &&
        typeof hoveredProductTableRow.primary_image_src === 'string' &&
        hoveredProductTableRow.primary_image_src.length > 0 &&
        createPortal(
          <div
            className="product-analysis-table__image-hover-popover product-analysis-table__image-hover-popover--portal"
            style={{
              left: tableImagePopoverPos.left,
              bottom: tableImagePopoverPos.bottom
            }}
          >
            <img
              src={hoveredProductTableRow.primary_image_src}
              alt={hoveredProductTableRow.primary_image_alt || hoveredProductTableRow.title || ''}
            />
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

